/**
 * Print rasters: the point cloud rendered top-down into EXACTLY the pixel
 * grid a sheet viewport needs (`round(mm / 25.4 · dpi)` per side), so the
 * pixel density on paper is the chosen DPI.
 *
 * - The camera is synthetic (orthographic, centered on the frame, turned by
 *   the sheet heading), independent of the on-screen camera.
 * - Large rasters are rendered in tiles whose frustums sit exactly on pixel
 *   boundaries, so the assembled image has no seams.
 * - Before rendering, the frame is loaded at full density (LOD focus region
 *   + raised point budget, the AutoFloorTool focused load); the on-screen
 *   LOD would look sparse at print resolution.
 * - Density (X-ray) accumulates exact per-pixel point counts in a float
 *   target. Its per-point alpha is rescaled from the on-screen tuning so the
 *   print is as dark as the screen at any DPI (see `printDensityAlpha`), and
 *   it prints as ink on white paper (dense = dark).
 *
 * The main view's material/clip overrides (view mode, floor isolation,
 * heatmap) apply exactly as on screen; callers put the viewer in the right
 * view first.
 */

import * as THREE from 'three';
import { ClipTask, PointSizeType } from '../defines';
import { DensityCompositeMaterial } from '../materials/DensityCompositeMaterial';
import { lastFramePendingNodes, setLodFocusBox } from '../Potree_update_visibility';
import * as potreeState from '../potreeState';
import { Utils } from '../utils';
import { densityAccumulationType } from '../viewer/PotreeRenderer';
import type { Viewer } from '../viewer/viewer';
import { applyMainViewOverrides } from './ScreenshotRenderer';
import { BoxVolume } from './Volume';
import { printDensityAlpha, rasterTiles } from './printRasterMath';

/** The world rectangle a viewport prints: center, page-up heading, size in meters. */
export interface PrintRasterFrame {
  center: { x: number; y: number };
  /** Degrees counter-clockwise from world +Y pointing up the page. */
  headingDeg: number;
  widthM: number;
  heightM: number;
}

export interface PrintDensitySettings {
  /** Per-point alpha as tuned on screen. */
  alpha: number;
  referenceMetersPerPixel: number;
  referencePointsPerM2: number | null;
}

export interface PrintRasterOptions {
  frame: PrintRasterFrame;
  pixelWidth: number;
  pixelHeight: number;
  mode: 'density' | 'color';
  /** Required for `density`. */
  density?: PrintDensitySettings;
  /** Meters per screen pixel when framed: fixed-size points keep their ground footprint. */
  screenMetersPerPixel: number | null;
  /**
   * How the frame's points are loaded first: `full` (default) at a raised
   * budget until settled, `quick` (previews) at the user's budget for a few
   * seconds, `none` renders what is loaded now.
   */
  load?: PrintRasterLoad;
  signal?: AbortSignal;
  onProgress?: (stage: 'loading' | 'rendering', fraction: number) => void;
}

/** Row-major, top row first: 1 channel (gray) or 3 (RGB). */
export interface PrintRaster {
  width: number;
  height: number;
  channels: 1 | 3;
  data: Uint8Array;
}

export type PrintRasterLoad = 'full' | 'quick' | 'none';

/** Point budget while the frame loads at full density (a higher user budget wins). */
const PRINT_POINT_BUDGET = 9_000_000;
/** Same settle rules as the AutoFloorTool focused load. */
const LOAD_SETTLED_FRAMES = 30;
const LOAD_STUCK_MS = 3_000;
const LOAD_TIMEOUT_MS = 60_000;
/** Quick (preview) loads: the frame gets the user's budget, briefly. */
const QUICK_LOAD_SETTLED_FRAMES = 10;
const QUICK_LOAD_TIMEOUT_MS = 8_000;
/** Largest tile side; the GPU's own limits may lower it. */
const MAX_TILE = 4096;
/** Refuse rasters whose pixel buffer alone would exceed this. */
export const PRINT_RASTER_MAX_BYTES = 600 * 1024 * 1024;

export class PrintRasterAbortError extends Error {
  constructor(message = 'Print raster cancelled') {
    super(message);
    this.name = 'PrintRasterAbortError';
  }
}

const toRadians = (deg: number) => (deg * Math.PI) / 180;

/** World XY → frame-local coordinates (x right, y up the page), meters from the center. */
function frameLocal(frame: PrintRasterFrame, x: number, y: number): { u: number; v: number } {
  const h = toRadians(frame.headingDeg);
  const dx = x - frame.center.x;
  const dy = y - frame.center.y;
  // Page right = (cos h, sin h), page up = (−sin h, cos h).
  return { u: dx * Math.cos(h) + dy * Math.sin(h), v: -dx * Math.sin(h) + dy * Math.cos(h) };
}

/** The frame's axis-aligned world bounds over the clouds' height. */
function frameWorldBox(viewer: Viewer, frame: PrintRasterFrame): THREE.Box3 {
  const h = toRadians(frame.headingDeg);
  const halfX = (Math.abs(Math.cos(h)) * frame.widthM + Math.abs(Math.sin(h)) * frame.heightM) / 2;
  const halfY = (Math.abs(Math.sin(h)) * frame.widthM + Math.abs(Math.cos(h)) * frame.heightM) / 2;
  const clouds = viewer.getBoundingBox(viewer.scene.pointclouds);
  return new THREE.Box3(new THREE.Vector3(frame.center.x - halfX, frame.center.y - halfY, clouds.min.z), new THREE.Vector3(frame.center.x + halfX, frame.center.y + halfY, clouds.max.z));
}

/**
 * Points per m² currently loaded inside the frame (all visible nodes, every
 * point counted). Measured when the user tunes X-ray darkness and again at
 * print time; the ratio says how much denser the print's point set is.
 */
export function framePointsPerM2(viewer: Viewer, frame: PrintRasterFrame): number {
  const box = frameWorldBox(viewer, frame);
  const nodeBox = new THREE.Box3();
  const p = new THREE.Vector3();
  let count = 0;
  for (const pc of viewer.scene.pointclouds) {
    if (!pc.visible) continue;
    for (const node of pc.visibleNodes) {
      if (!node.sceneNode || !node.geometryNode?.geometry) continue;
      const position = node.geometryNode.geometry.attributes.position as THREE.BufferAttribute | undefined;
      if (!position?.array) continue;
      nodeBox.copy(node.geometryNode.boundingBox).applyMatrix4(pc.matrixWorld);
      if (!nodeBox.intersectsBox(box)) continue;
      const matrix = node.sceneNode.matrixWorld;
      for (let i = 0; i < position.count; i++) {
        p.fromBufferAttribute(position, i).applyMatrix4(matrix);
        const { u, v } = frameLocal(frame, p.x, p.y);
        if (Math.abs(u) <= frame.widthM / 2 && Math.abs(v) <= frame.heightM / 2) count++;
      }
    }
  }
  return count / Math.max(1e-9, frame.widthM * frame.heightM);
}

const nextFrame = () => new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) throw new PrintRasterAbortError();
}

/**
 * Concentrate the point budget on the frame and wait until it is fully
 * loaded. Returns the function that puts budget, focus and clip back, and
 * the focus clip's id: the render must leave that clip out — isolate volumes
 * combine as a union, so the full-height box would re-admit what floor
 * isolation hides (the roof over the floor).
 */
async function loadFrame(viewer: Viewer, frame: PrintRasterFrame, mode: 'full' | 'quick', signal: AbortSignal | undefined, onProgress: ((fraction: number) => void) | undefined): Promise<{ restore: () => void; clipId: string }> {
  const timeoutMs = mode === 'full' ? LOAD_TIMEOUT_MS : QUICK_LOAD_TIMEOUT_MS;
  const settledFrames = mode === 'full' ? LOAD_SETTLED_FRAMES : QUICK_LOAD_SETTLED_FRAMES;
  const box = frameWorldBox(viewer, frame);
  // A SHOW_INSIDE box over the frame throws the rest of the scene out of the
  // LOD pass, so the whole (raised) budget goes to the printed area.
  const clip = new BoxVolume({
    position: new THREE.Vector3(frame.center.x, frame.center.y, (box.min.z + box.max.z) / 2),
    scale: new THREE.Vector3(frame.widthM, frame.heightM, box.max.z - box.min.z + 1),
    rotation: new THREE.Vector3(0, 0, toRadians(frame.headingDeg)),
    clip: true,
  });
  clip.assignedClipTask = ClipTask.SHOW_INSIDE;
  clip.actualClipTask = ClipTask.SHOW_INSIDE;
  clip.visible = false;
  clip.updateMatrixWorld(true);
  viewer.scene.addVolume(clip);

  const savedBudget = viewer.getPointBudget();
  const savedDynamic = viewer.isDynamicPointBudgetEnabled();
  if (mode === 'full') {
    if (savedDynamic) viewer.disableDynamicPointBudget();
    viewer.setPointBudget(Math.max(PRINT_POINT_BUDGET, savedBudget));
  }
  setLodFocusBox(box);

  const restore = () => {
    setLodFocusBox(null);
    viewer.scene.removeVolume(clip);
    if (mode === 'full') {
      viewer.setPointBudget(savedBudget);
      if (savedDynamic) viewer.enableDynamicPointBudget();
    }
  };

  try {
    const startedAt = performance.now();
    let settled = 0;
    let stuckCount = -1;
    let stuckSince = 0;
    while (performance.now() - startedAt < timeoutMs) {
      await nextFrame();
      throwIfAborted(signal);
      const pending = lastFramePendingNodes;
      const loading = potreeState.numNodesLoading;
      onProgress?.(Math.min(0.95, (performance.now() - startedAt) / timeoutMs));
      if (pending === 0 && loading === 0) {
        if (++settled >= settledFrames) break;
      } else {
        settled = 0;
      }
      if (pending === 0 && loading > 0 && loading === stuckCount) {
        if (performance.now() - stuckSince > LOAD_STUCK_MS) break;
      } else {
        stuckCount = pending === 0 ? loading : -1;
        stuckSince = performance.now();
      }
    }
    onProgress?.(1);
    return { restore, clipId: clip.identifier };
  } catch (error) {
    restore();
    throw error;
  }
}

/** Synthetic top-down camera over the frame, turned so page-up is the frame's up. */
function frameCamera(viewer: Viewer, frame: PrintRasterFrame): THREE.OrthographicCamera {
  const clouds = viewer.getBoundingBox(viewer.scene.pointclouds);
  const top = clouds.max.z + 1;
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.01, top - clouds.min.z + 2);
  const h = toRadians(frame.headingDeg);
  camera.position.set(frame.center.x, frame.center.y, top);
  camera.up.set(-Math.sin(h), Math.cos(h), 0);
  camera.lookAt(frame.center.x, frame.center.y, top - 1);
  camera.updateMatrixWorld(true);
  return camera;
}

function tileLimit(renderer: THREE.WebGLRenderer): number {
  const gl = renderer.getContext();
  const renderbuffer = gl.getParameter(gl.MAX_RENDERBUFFER_SIZE) as number;
  return Math.max(256, Math.min(MAX_TILE, renderer.capabilities.maxTextureSize, renderbuffer));
}

interface MaterialSizes {
  size: number;
  minSize: number;
  maxSize: number;
}

/**
 * Render the frame into a `pixelWidth × pixelHeight` raster. The viewer must
 * already show the right view (mode, floor); its overrides are applied as on
 * screen.
 */
export async function renderPrintRaster(viewer: Viewer, options: PrintRasterOptions): Promise<PrintRaster> {
  const { frame, pixelWidth: width, pixelHeight: height, signal } = options;
  const channels = options.mode === 'density' ? 1 : 3;
  if (width < 1 || height < 1) throw new Error('Print raster has no pixels');
  if (width * height * channels > PRINT_RASTER_MAX_BYTES) throw new Error(`Print raster of ${width} × ${height} px is too large; lower the DPI`);
  const renderer = viewer.renderer;
  const accumulation = options.mode === 'density' ? densityAccumulationType(renderer.getContext()) : null;
  if (options.mode === 'density' && (!options.density || accumulation === null)) throw new Error('Density prints need float render targets, which this GPU does not support');

  let contextLost = false;
  const onContextLost = () => {
    contextLost = true;
  };
  renderer.domElement.addEventListener('webglcontextlost', onContextLost);

  let restoreLoad = () => undefined as void;
  const hiddenClipIds: string[] = [];
  try {
    const load = options.load ?? 'full';
    if (load !== 'none') {
      const loaded = await loadFrame(viewer, frame, load, signal, (f) => options.onProgress?.('loading', f));
      restoreLoad = loaded.restore;
      hiddenClipIds.push(loaded.clipId);
    }
  } catch (error) {
    renderer.domElement.removeEventListener('webglcontextlost', onContextLost);
    throw error;
  }
  const mppX = frame.widthM / width;
  const mppY = frame.heightM / height;
  const output = new Uint8Array(width * height * channels);
  const camera = frameCamera(viewer, frame);
  const tile = tileLimit(renderer);
  const tiles = rasterTiles(width, height, tile);

  let alpha = 0;
  if (options.mode === 'density' && options.density) {
    const fixedPoints = viewer.scene.pointclouds.every((pc) => pc.material.pointSizeType === PointSizeType.FIXED);
    alpha = printDensityAlpha({
      ...options.density,
      printMetersPerPixel: mppX,
      pointAreaScales: fixedPoints,
      printPointsPerM2: options.density.referencePointsPerM2 ? framePointsPerM2(viewer, frame) : null,
    });
  }

  const colorTarget = new THREE.WebGLRenderTarget(1, 1, { minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, format: THREE.RGBAFormat, depthBuffer: true });
  const densityTarget =
    accumulation === null ? null : new THREE.WebGLRenderTarget(1, 1, { minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, format: THREE.RGBAFormat, type: accumulation, depthBuffer: false, stencilBuffer: false });
  const composite = densityTarget ? new DensityCompositeMaterial() : null;
  composite?.setAlpha(alpha);
  const pixels = new Uint8Array(Math.min(tile, width) * Math.min(tile, height) * 4);
  const previousTarget = renderer.getRenderTarget();
  const sizes = new Map<object, MaterialSizes>();

  try {
    for (let t = 0; t < tiles.length; t++) {
      // Yield between tiles: the page stays responsive and the abort / context-loss checks run.
      await nextFrame();
      throwIfAborted(signal);
      if (contextLost) throw new Error('The graphics context was lost while rendering the print');
      const { x, y, w, h } = tiles[t];
      camera.left = -frame.widthM / 2 + x * mppX;
      camera.right = camera.left + w * mppX;
      camera.top = frame.heightM / 2 - y * mppY;
      camera.bottom = camera.top - h * mppY;
      camera.updateProjectionMatrix();

      const clouds = viewer.scene.pointclouds.filter((pc) => pc.visible);
      const restoreOverrides = applyMainViewOverrides(viewer, clouds, hiddenClipIds);
      try {
        for (const pc of clouds) {
          const material = pc.material;
          material.weighted = false;
          material.useLogarithmicDepthBuffer = false;
          material.useEDL = false;
          material.screenWidth = w;
          material.screenHeight = h;
          material.uniforms.visibleNodes.value = material.visibleNodesTexture;
          material.uniforms.octreeSize.value = pc.pcoGeometry.boundingBox.getSize(new THREE.Vector3()).x;
          material.spacing = pc.pcoGeometry.spacing;
          // Points keep their ground footprint: N screen px at the framing zoom = N·(mpp_screen / mpp_print) print px.
          if (options.mode === 'color' && options.screenMetersPerPixel) {
            const ratio = Math.max(1, options.screenMetersPerPixel / mppX);
            const maxSize = material.uniforms.maxSize.value as number;
            sizes.set(material, { size: material.size, minSize: material.minSize, maxSize });
            if (material.pointSizeType === PointSizeType.FIXED) material.size = material.size * ratio;
            material.minSize = material.minSize * ratio;
            material.uniforms.maxSize.value = maxSize * ratio;
          }
        }

        if (densityTarget && composite) {
          densityTarget.setSize(w, h);
          renderer.setRenderTarget(densityTarget);
          renderer.setClearColor(0x000000, 0);
          renderer.clear(true, false, false);
          const gl = renderer.getContext();
          viewer.pRenderer.render(viewer.scene.scenePointCloud, camera, densityTarget, {
            orderedIdentifiers: viewer.scene.orderedIdentifiers || [],
            blendFunc: [gl.ONE, gl.ONE],
            depthTest: false,
            depthWrite: false,
          });
          colorTarget.setSize(w, h);
          renderer.setRenderTarget(colorTarget);
          renderer.setClearColor(0x000000, 1);
          renderer.clear(true, true, false);
          composite.setDensityTexture(densityTarget.texture);
          Utils.screenPass.render(renderer, composite, colorTarget);
        } else {
          colorTarget.setSize(w, h);
          renderer.setRenderTarget(colorTarget);
          renderer.setClearColor(0x000000, 0);
          renderer.clear(true, true, false);
          viewer.pRenderer.render(viewer.scene.scenePointCloud, camera, colorTarget, { orderedIdentifiers: viewer.scene.orderedIdentifiers || [] });
        }
        renderer.readRenderTargetPixels(colorTarget, 0, 0, w, h, pixels);
      } finally {
        for (const pc of clouds) {
          const saved = sizes.get(pc.material);
          if (!saved) continue;
          pc.material.size = saved.size;
          pc.material.minSize = saved.minSize;
          pc.material.uniforms.maxSize.value = saved.maxSize;
        }
        sizes.clear();
        restoreOverrides();
      }

      // GL rows run bottom-up; the raster runs top-down.
      for (let row = 0; row < h; row++) {
        const src = (h - 1 - row) * w * 4;
        let dst = ((y + row) * width + x) * channels;
        for (let col = 0; col < w; col++, dst += channels) {
          const s = src + col * 4;
          if (channels === 1) {
            // Ink on paper: brightness (density) prints dark.
            output[dst] = 255 - pixels[s];
          } else {
            // Over white paper.
            const a = pixels[s + 3] / 255;
            output[dst] = Math.round(pixels[s] * a + 255 * (1 - a));
            output[dst + 1] = Math.round(pixels[s + 1] * a + 255 * (1 - a));
            output[dst + 2] = Math.round(pixels[s + 2] * a + 255 * (1 - a));
          }
        }
      }
      options.onProgress?.('rendering', (t + 1) / tiles.length);
    }
  } finally {
    renderer.setRenderTarget(previousTarget);
    colorTarget.dispose();
    densityTarget?.dispose();
    composite?.dispose();
    restoreLoad();
    renderer.domElement.removeEventListener('webglcontextlost', onContextLost);
  }

  return { width, height, channels, data: output };
}
